import Head from 'next/head'
import { useState } from 'react'
import { GetServerSideProps } from 'next/types'

import googleMaps from '@/services/google-maps'
import homeValuation from '@/services/home-valuation-v2'
import PropertySearch from '@/components/PropertySearch'
import ValuationDisplay from '@/components/ValuationDisplay'
import { PropertyReport } from '@/types/property'

interface Props {
  query_address: string
  error_message?: string
  normalized_address: string
  property_report?: PropertyReport
  coordinates?: google.maps.GeocoderGeometry['location']
}

const Home = (props: Props) => {
  const [isPropertySearch, setIsPropertySearch] = useState(
    !props.query_address || !props.property_report || !props.normalized_address
  )
  const [property, setProperty] = useState(props.property_report)
  const [marker, setMarkerPosition] = useState(props.normalized_address)

  const handleSelection = (
    selected_address: string,
    property_report: PropertyReport
  ) => {
    setMarkerPosition(selected_address)
    setProperty(property_report)
    setIsPropertySearch(false)
  }

  return (
    <div id="home-valuation-tool">
      <Head>
        <title>Home Valuation Tool | Bankrate</title>
      </Head>
      {!isPropertySearch ? (
        <ValuationDisplay
          property_address={marker}
          property_report={property}
          coordinates={props.coordinates}
          onEditToggle={() => setIsPropertySearch(true)}
        />
      ) : (
        <PropertySearch
          initial_address={props.query_address || marker}
          error_message={props.error_message}
          onSelection={handleSelection}
        />
      )}
      {process.env.NODE_ENV === 'development' && (
        <div
          id="home-valuation-tool-footer"
          className="text-center text-slate type-body-three"
          data-comment="this lives in fantasia, keeping it here for local development"
        ></div>
      )}
    </div>
  )
}

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
  const query_address = (query.address as string) || ''

  if (!query_address) {
    return {
      props: {
        query_address,
        normalized_address: null
      }
    }
  }

  /**
   * Validate address using google maps api
   */
  const { results } = await googleMaps.geocodeAddress(query_address)

  /**
   * If address is valid, fetch data from API and pass validated address
   * to front end.
   * When a single result is returned from google maps api, we can assume that
   * the address input was a complete address and can use it
   */
  if (results.length === 1) {
    const normalized_address = results[0].formatted_address?.replace(
      ', USA',
      ''
    )
    const coordinates = results[0].geometry.location

    const property_report = await homeValuation.getHomeValuation(
      normalized_address
    )

    return {
      props: {
        query_address,
        coordinates,
        normalized_address,
        property_report,
        error_message: property_report
          ? null
          : 'We were unable to find a home valuation for this address. Please try again.'
      }
    }
  }

  /**
   * When we get no results or more than 1 it means that the address was invalid
   * so users will need to verify the address and geocoding will happen client
   * side.
   * An error state needs to be shown.
   */
  if (results.length !== 1) {
    return {
      props: {
        error_message: 'Invalid address provided, please try again.',
        query_address,
        normalized_address: null
      }
    }
  }

  /**
   * step 1. Validate address using google maps api
   * step 2. If address is valid, fetch data from API and pass validated address to front end
   * step 3. If address is invalid, return error and show main view
   * step 4. Once results are received pass them to component as props
   * step 5. if there is no address then render main view
   */
  return {
    props: {
      query_address
    }
  }
}

export default Home
